<template>
  <div>
    <Bar
      :style="props.barStyles"
      :options="options"
      :data="props.data"
    />
    <div
      ref="tooltipEl" class="tooltip" role="tooltip"
      style="position: absolute; pointer-events: none; opacity: 0; transition: opacity .15s; z-index: 10;"
    >
      <!-- <div ref="tooltipArrow" class="tooltip-arrow"></div> -->
      <div ref="tooltipInner" class="tooltip-inner" style="max-width: none; text-align: left;"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import Chart, { type TooltipModel } from 'chart.js/auto';
  // import * as ChartHelper from '@/compositions/chartHelper';
  import { Tooltip, type ChartData, type ChartOptions, type ChartEvent, type ActiveElement } from 'chart.js'
  import { Bar } from 'vue-chartjs'
  import ChartDataLabels from 'chartjs-plugin-datalabels';
  import { ref } from 'vue';
  Chart.register(ChartDataLabels);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Tooltip.positioners as any).cursor = function(_chartElements: any, coordinates: any) {
    return coordinates;
  };

  const emits = defineEmits<{
    (e: 'click',
      label: string | undefined,
      value: number | [number, number] | undefined | null,
      index: number,
      event: ChartEvent, elements: ActiveElement[],
      chart: Chart
    ): void
  }>();

  const props = defineProps<{
    data: ChartData<'bar', (number | [number, number] | null)[], unknown>,
    barStyles?: string,
    hideZeroTooltipItems?: boolean,
  }>();

  const tooltipEl = ref<HTMLElement | null>(null)
  // const tooltipArrow = ref<HTMLElement | null>(null)
  const tooltipInner = ref<HTMLElement | null>(null)

  function externalTooltipHandler(context: { chart: Chart; tooltip: TooltipModel<'bar'> }) {
    const { chart, tooltip } = context
    const el    = tooltipEl.value
    const inner = tooltipInner.value
    if (!el || !inner) return

    if (tooltip.opacity === 0) {
      el.style.opacity = '0'
      return
    }

    // Placement dynamique Bootstrap : bs-tooltip-top ou bs-tooltip-bottom
    el.className = 'tooltip show ' + (tooltip.yAlign === 'bottom' ? 'bs-tooltip-bottom' : 'bs-tooltip-top')

    // Contenu : une ligne par dataset avec indicateur coloré + total
    const dataPoints = props.hideZeroTooltipItems
      ? tooltip.dataPoints.filter(dp => dp.parsed.x !== 0)
      : tooltip.dataPoints;
    if (dataPoints.length === 0) {
      el.style.opacity = '0';
      return;
    }

    const total = dataPoints.reduce((sum, dp) => sum + (dp.parsed.x as number), 0)
    inner.innerHTML =
      dataPoints.map((dp) => {
        const bg = (dp.dataset.backgroundColor as string[])[0]
        const bd = (dp.dataset.borderColor as string[])[0]
        return `<div class="d-flex align-items-center gap-2 text-nowrap">
          <span style="width:10px;height:10px;background:${bg};border:1px solid ${bd};
                       display:inline-block;border-radius:2px;flex-shrink:0;"></span>
          <span>${dp.dataset.label} : <b>${dp.parsed.x}</b></span>
        </div>`
      }).join('') +
      `<div class="border-top mt-1 pt-1">Total : <b>${total}</b></div>`

    // Positionnement relatif au .card-body parent
    const canvasRect = chart.canvas.getBoundingClientRect()
    const parentRect = el.parentElement!.getBoundingClientRect()
    const tooltipW   = el.offsetWidth || 150
    const tooltipH   = el.offsetHeight || 60
    const parentW    = el.parentElement!.offsetWidth

    const rawLeft = canvasRect.left - parentRect.left + tooltip.caretX - tooltipW / 2
    const left = Math.max(0, Math.min(rawLeft, parentW - tooltipW))
    const top  = tooltip.yAlign === 'bottom'
      ? canvasRect.top - parentRect.top + tooltip.caretY + 8
      : canvasRect.top - parentRect.top + tooltip.caretY - tooltipH - 8

    el.style.left    = `${left}px`
    el.style.top     = `${top}px`
    el.style.opacity = '1'
  }

  const options = <ChartOptions<"bar">>{
    indexAxis: "y" as const,
    borderWidth: 0,
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        display: false,
      },
      y: {
        beginAtZero: true,
        stacked: true,
        display: false,
      }
    },
    onClick(event, elements, chart) {
      if (elements.length > 0) {
        const element = elements[0];
        const datasetIndex = element.datasetIndex;
        const dataIndex = element.index;
        const dataset = props.data.datasets[datasetIndex];
        const xValue = dataset.label;
        const yValue = dataset.data[dataIndex];
        emits('click', xValue, yValue, datasetIndex, event, elements, chart)
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        formatter: (v: number) => v ? v : '', // n'affiche pas les 0
        color: 'white',
        display: true,
        font: {
          size: 16,
          weight: 'bold' as const
        },
      },
      tooltip: {
        enabled: false,
        mode: 'index' as const,
        axis: 'y' as const,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        position: (() => 'cursor') as any,
        intersect: false,
        external: externalTooltipHandler
      }
    }
  }
</script>
